import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { tokens } from '../theme';
import { useTheme } from '@emotion/react';

/**
 * Reusable pagination component for consistent pagination UI across the application
 * 
 * @param {Object} props
 * @param {Object} props.paginationInfo - Object containing pagination details
 * @param {number} props.paginationInfo.currentPage - Current page number
 * @param {number} props.paginationInfo.rowsPerPage - Number of rows per page
 * @param {number} props.paginationInfo.total - Total number of items
 * @param {boolean} props.isLoading - Whether data is currently being fetched
 * @param {Function} props.onPageChange - Callback function when page changes
 * @param {Function} props.onRowsPerPageChange - Callback function when rows per page changes
 * @param {Array<number>} props.pageSizeOptions - Available page size options
 * @returns {JSX.Element} Pagination UI component
 */
const PaginationUI = ({
  paginationInfo,
  isLoading,
  onPageChange,
  onRowsPerPageChange,
  pageSizeOptions = [5, 10, 25, 50]
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const totalPages = Math.ceil(paginationInfo.total / paginationInfo.rowsPerPage) || 1;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Page navigation */}
        <Button 
          disabled={paginationInfo.currentPage <= 1 || isLoading}
          onClick={() => onPageChange(paginationInfo.currentPage - 1, paginationInfo.rowsPerPage)}
          variant="outlined"
          size="small"
        >
          Previous
        </Button>
        
        <Typography variant="body2">
          Page {paginationInfo.currentPage} of {totalPages}
        </Typography>
        
        <Button 
          disabled={paginationInfo.currentPage >= totalPages || isLoading}
          onClick={() => onPageChange(paginationInfo.currentPage + 1, paginationInfo.rowsPerPage)}
          variant="outlined"
          size="small"
        >
          Next
        </Button>
        
        {/* Rows per page */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 4 }}>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Rows per page:
          </Typography>
          <select
            value={paginationInfo.rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            style={{
              backgroundColor: colors.grey[70],
              padding: '5px',
              borderRadius: '4px',
              border: `1px solid ${colors.grey[400]}`
            }}
          >
            {pageSizeOptions.map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </Box>
      </Box>
      
      {/* Loading indicator can be added by the parent component */}
    </Box>
  );
};

export default PaginationUI;
